import FilterListIcon from '@mui/icons-material/FilterList';
import { Box } from '@mui/material';
import React, { forwardRef, useCallback, useImperativeHandle, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  ClearFiltersButton,
  DepartmentFilter,
  FilterSelect,
  InfiniteScrollSelect,
  ViewSwitcher,
} from '../../../components/_mui';
import {
  containerStyles,
  filtersContainerStyles,
  filtersRowStyles,
  headerStyles,
  searchContainerStyles,
} from '../../../components/_mui/styles/TableFilters.styles';
import { CUSTOM_FILTER_OPTION } from '../../../constants/campaigns';
import { DISTRIBUTOR, ORG_ADMIN, SUPER_ADMIN } from '../../../constants/users';
import { changeOneLinkActiveView } from '../../../store/actions/shell';
import { selectAdminType, selectIsAdminTypeUser } from '../../../store/selectors/auth';
import { selectCurrentOrganizationDepartmentList } from '../../../store/selectors/organizations';
import { selectOneLinksActiveView } from '../../../store/selectors/shell';
import { FilterTypesEnum, ViewTypesEnum } from '../../../types/shell';
import { IFilterItem, IFiltersRef } from '../../../types/tables';
import { hasPermission } from '../../../utils/users';

interface IProps {
  renderSearchField: React.JSX.Element | null;
  onSearchReset: () => void;
  query?: string;
  selectedDepartments: string[];
  handleSelectDepartments: (departments: string[]) => void;
  filterConfig: IFilterItem[];
  selectedItemName?: string;
  setSelectedItemName?: (name: string | undefined) => void;
  selectedCampaignName?: string;
  setSelectedCampaignName?: (name: string | undefined) => void;
}

const Filters = forwardRef<IFiltersRef, IProps>(
  (
    {
      renderSearchField,
      onSearchReset,
      query,
      selectedDepartments,
      handleSelectDepartments,
      filterConfig,
      selectedItemName,
      setSelectedItemName,
      selectedCampaignName,
      setSelectedCampaignName,
    }: IProps,
    filtersRef,
  ) => {
    const dispatch = useDispatch();
    const adminType = useSelector(selectAdminType);
    const isAdminTypeUser = useSelector(selectIsAdminTypeUser);
    const activeView = useSelector(selectOneLinksActiveView);
    const allDepartments = useSelector(selectCurrentOrganizationDepartmentList) ?? [];

    const isDeptFilterShown = useMemo(
      () => isAdminTypeUser && hasPermission([ORG_ADMIN, SUPER_ADMIN, DISTRIBUTOR], adminType),
      [isAdminTypeUser, hasPermission, adminType],
    );

    const handleSetActiveView = useCallback(
      (view: ViewTypesEnum) => {
        dispatch(changeOneLinkActiveView(view));
      },
      [dispatch],
    );

    const isAnyFilterActive = useMemo(() => {
      return (
        filterConfig.some((filter) => filter.value && filter?.value?.length > 0) ||
        (query && query?.length > 0) ||
        selectedDepartments.length > 0
      );
    }, [filterConfig, query, selectedDepartments]);

    const handleClearFilter = useCallback((onChange: (value: string | string[] | undefined) => void) => {
      onChange('');
    }, []);

    const handleClearAllFilters = useCallback(() => {
      filterConfig.forEach((filter) => filter.onChange(''));
      handleSelectDepartments([]);

      if (onSearchReset) {
        onSearchReset();
      }
    }, [filterConfig, handleSelectDepartments, onSearchReset]);

    useImperativeHandle(
      filtersRef,
      () => ({
        resetFilters: handleClearAllFilters,
      }),
      [handleClearAllFilters],
    );

    return (
      <Box sx={containerStyles}>
        <Box sx={headerStyles}>
          <Box sx={searchContainerStyles}>{renderSearchField}</Box>
        </Box>
        <Box sx={filtersRowStyles}>
          <Box sx={filtersContainerStyles}>
            <FilterListIcon color="disabled" />
            {filterConfig.map(
              ({
                tabId,
                label,
                value,
                onChange,
                options,
                isMultiple = false,
                isVisible = true,
                isIconVisible = true,
                limit,
                onLimitReached,
                useInfiniteScroll = false,
                autocompleteConfig,
                dataHook,
                dataAction,
              }) => {
                const isItemFilter = tabId === FilterTypesEnum.Item;
                const isCampaignFilter = tabId === FilterTypesEnum.Campaign;
                const handleName = isItemFilter
                  ? setSelectedItemName
                  : isCampaignFilter
                  ? setSelectedCampaignName
                  : undefined;
                const name = isItemFilter ? selectedItemName : isCampaignFilter ? selectedCampaignName : undefined;
                const isInfiniteScrollSelector = useInfiniteScroll && autocompleteConfig && dataHook;
                const isStatusFilter = tabId === FilterTypesEnum.Status;

                return (
                  <React.Fragment key={tabId}>
                    {isVisible ? (
                      isInfiniteScrollSelector ? (
                        <InfiniteScrollSelect
                          label={label}
                          value={value as string | undefined}
                          onChange={onChange}
                          onClear={handleClearFilter}
                          onNameChange={handleName}
                          displayName={name}
                          isIconVisible={isIconVisible}
                          config={autocompleteConfig}
                          useDataHook={dataHook}
                          dataAction={dataAction}
                          staticOptions={isCampaignFilter ? CUSTOM_FILTER_OPTION : undefined}
                        />
                      ) : (
                        <FilterSelect
                          limit={limit}
                          onLimitReached={onLimitReached}
                          isMultiple={isMultiple}
                          label={label}
                          value={value}
                          onChange={onChange}
                          options={options}
                          onClear={handleClearFilter}
                          dataAction={dataAction}
                        />
                      )
                    ) : null}
                    {isStatusFilter && isDeptFilterShown && (
                      <DepartmentFilter
                        value={selectedDepartments}
                        onChange={handleSelectDepartments}
                        departments={allDepartments}
                      />
                    )}
                  </React.Fragment>
                );
              },
            )}
            {isAnyFilterActive && <ClearFiltersButton onClick={handleClearAllFilters} />}
          </Box>
          <ViewSwitcher activeView={activeView} setActiveView={handleSetActiveView} />
        </Box>
      </Box>
    );
  },
);

export default Filters;
